import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, clearToken, getToken, setToken } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = logged out, object = user
  const [user, setUser] = useState(getToken() ? null : false);
  const [loading, setLoading] = useState(!!getToken());

  useEffect(() => {
    let cancel = false;
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    api.me()
      .then((u) => { if (!cancel) setUser(u); })
      .catch((err) => {
        if (cancel) return;
        // Token invalid / expired — drop it and treat the user as logged out.
        if (process.env.NODE_ENV !== 'production') {
          console.warn('AuthContext: session check failed', err);
        }
        clearToken();
        setUser(false);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
    // Intentional empty deps: only run once on mount. `api`, `getToken`,
    // `clearToken`, `setUser`, `setLoading` are stable module-level / setter refs.
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await api.login(email, password);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (err) {
      // Logout failure is non-fatal — we still clear local state.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('AuthContext: server logout failed', err);
      }
    }
    clearToken();
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
